import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { v4 as uuidv4 } from "uuid";
import { addAuthHeader, handle401Error } from "../utils/auth/axiosInterceptor";

/**
 * Default HTTPS instance for general API calls
 */
const https = axios.create({
  baseURL: import.meta.env.VITE_REACT_LOGIN_APP_API || "http://login.impact0mics.local:9003",
  timeout: 50000,
});

// Add request interceptor to generate unique headers and add Authorization
https.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Generate unique X-Message-Id and X-Correlation-Id for each request
    if (config.headers) {
      config.headers["X-Message-Id"] = uuidv4();
      config.headers["X-Correlation-Id"] = uuidv4();
    }
    // Add Authorization header with access token (skip for login/auth endpoints)
    const url = config.url || "";
    if (!url.includes("/auth/login") && !url.includes("/auth/token/refresh")) {
      return addAuthHeader(config);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
https.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Skip token refresh for login/refresh endpoints to avoid infinite loop
      const url = originalRequest.url || "";
      if (url.includes("/auth/login") || url.includes("/auth/token/refresh")) {
        return Promise.reject(error);
      }

      try {
        return await handle401Error(error, (config) => https.request(config));
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Add response interceptor to log responses for debugging
https.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful responses to console for debugging
    console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      headers: response.headers,
    });
    return response;
  },
  (error) => {
    // Log error responses to console for debugging
    if (error.response) {
      console.error(`[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers,
      });
    } else {
      console.error('[API Error] Network Error', error.message);
    }
    return Promise.reject(error);
  }
);

export default https;

// Export IAM-specific instance
export { default as iamHttps } from "./iam";
